import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowRight,
  CheckCircle,
  CreditCard,
  Gamepad2,
  Gift,
  History,
  Monitor,
  Package,
  Search,
  Send,
  Tv,
  Wallet,
  X,
  Zap
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { TransactionService } from '../../services/transactionService';
import { ProductService } from '../../services/productService';
import { CategoryService } from '../../services/categoryService';
import LoadingSpinner from '../common/LoadingSpinner';
import { useNotification } from '../common/Notification';

const toDateValue = (value) => {
  if (!value) return null;

  if (typeof value.toDate === 'function') {
    return value.toDate();
  }

  const parsedDate = new Date(value);
  return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
};

const formatRelativeMobileDate = (value) => {
  const date = toDateValue(value);
  if (!date) return 'Date inconnue';

  const now = new Date();
  const sameDay = date.toDateString() === now.toDateString();
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);

  const timeLabel = date.toLocaleTimeString('fr-FR', {
    hour: '2-digit',
    minute: '2-digit'
  });

  if (sameDay) {
    return `Aujourd'hui a ${timeLabel}`;
  }

  if (date.toDateString() === yesterday.toDateString()) {
    return `Hier a ${timeLabel}`;
  }

  return date.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit'
  });
};

const isWithinLastHours = (value, hours = 24) => {
  const date = toDateValue(value);
  if (!date) return false;

  return Date.now() - date.getTime() <= hours * 60 * 60 * 1000;
};

const getActivityMeta = (operation) => {
  const status = String(operation.status || '').toLowerCase();
  const type = String(operation.type || '').toLowerCase();
  const reason = String(operation.reason || '').toLowerCase();

  if (type === 'recharge' || reason.includes('credit') || reason.includes('recharge')) {
    return {
      label: 'Recharge solde',
      badgeClass: 'bg-emerald-50 text-emerald-600',
      amountClass: 'text-emerald-600',
      amountPrefix: '+',
      icon: Wallet
    };
  }

  if (TransactionService.isBillPaymentTransaction(operation)) {
    return {
      label: TransactionService.getBillPaymentTransactionLabel(operation),
      badgeClass: status === 'completed' ? 'bg-emerald-50 text-emerald-600' : 'bg-amber-50 text-amber-600',
      amountClass: type === 'bill_payment_refund' ? 'text-emerald-600' : 'text-rose-600',
      amountPrefix: type === 'bill_payment_refund' ? '+' : '-',
      icon: FileTextIcon
    };
  }

  if (type === 'topup_order' || type === 'topup' || reason.includes('topup')) {
    return {
      label: operation.productName ? `Commande ${operation.productName}` : 'Commande topup',
      badgeClass: status === 'pending' ? 'bg-amber-50 text-amber-600' : 'bg-sky-50 text-sky-600',
      amountClass: type === 'topup_refund' ? 'text-emerald-600' : 'text-rose-600',
      amountPrefix: type === 'topup_refund' ? '+' : '-',
      icon: Zap
    };
  }

  if (status === 'pending' || status === 'processing') {
    return {
      label: operation.productName ? `Achat carte ${operation.productName}` : 'Operation en attente',
      badgeClass: 'bg-amber-50 text-amber-600',
      amountClass: 'text-rose-600',
      amountPrefix: '-',
      icon: Package
    };
  }

  if (status === 'refused' || status === 'failed' || status === 'rejected') {
    return {
      label: operation.productName ? `Achat carte ${operation.productName}` : 'Operation refusee',
      badgeClass: 'bg-rose-50 text-rose-600',
      amountClass: 'text-rose-600',
      amountPrefix: '-',
      icon: Package
    };
  }

  return {
    label: operation.productName ? `Achat carte ${operation.productName}` : 'Achat effectue',
    badgeClass: 'bg-emerald-50 text-emerald-600',
    amountClass: 'text-rose-600',
    amountPrefix: '-',
    icon: CreditCard
  };
};

const FileTextIcon = History;

const getCategoryPresentation = (categoryName) => {
  const normalized = String(categoryName || '').toLowerCase();

  if (normalized.includes('pc')) {
    return {
      icon: Monitor,
      iconClass: 'text-emerald-500 bg-emerald-50',
      actionClass: 'text-emerald-600'
    };
  }

  if (
    normalized.includes('stream') ||
    normalized.includes('tv') ||
    normalized.includes('music') ||
    normalized.includes('musique')
  ) {
    return {
      icon: Tv,
      iconClass: 'text-amber-500 bg-amber-50',
      actionClass: 'text-amber-600'
    };
  }

  if (normalized.includes('gift') || normalized.includes('cadeau')) {
    return {
      icon: Gift,
      iconClass: 'text-pink-500 bg-pink-50',
      actionClass: 'text-pink-600'
    };
  }

  if (normalized.includes('mobile') || normalized.includes('phone')) {
    return {
      icon: CreditCard,
      iconClass: 'text-cyan-500 bg-cyan-50',
      actionClass: 'text-cyan-600'
    };
  }

  return {
    icon: Gamepad2,
    iconClass: 'text-violet-500 bg-violet-50',
    actionClass: 'text-violet-600'
  };
};

const buildPopularCategories = (products, categories) => {
  const groupedCategories = new Map();

  ProductService.filterProductsBySection(products, categories, CategoryService.CATEGORY_SECTIONS.CARDS)
    .forEach((product) => {
      const categoryName = String(product.category || 'Autres').trim() || 'Autres';
      const categoryKey = categoryName.toLowerCase().replace(/\s+/g, '-');
      const existingEntry = groupedCategories.get(categoryKey) || {
        id: categoryKey,
        name: categoryName,
        count: 0
      };

      existingEntry.count += 1;
      groupedCategories.set(categoryKey, existingEntry);
    });

  return Array.from(groupedCategories.values())
    .sort((first, second) => second.count - first.count)
    .slice(0, 6);
};

const resolveProductImage = (product = {}) => {
  return String(product.coverImageUrl || product.imageUrl || product.detailImageUrl || '').trim();
};

const resolveProductMinPrice = (product = {}) => {
  const offers = Array.isArray(product.offers) ? product.offers : [];
  const prices = offers
    .map((offer) => Number(offer?.price || 0))
    .filter((price) => Number.isFinite(price) && price > 0);

  return prices.length > 0 ? Math.min(...prices) : 0;
};

const buildPopularProducts = (products, categories) => {
  return ProductService.filterProductsBySection(products, categories, CategoryService.CATEGORY_SECTIONS.CARDS)
    .map((product) => ({
      id: product.id,
      name: product.name || 'Produit',
      imageUrl: resolveProductImage(product),
      minPrice: resolveProductMinPrice(product),
      category: String(product.category || '').trim()
    }))
    .filter((product) => product.minPrice > 0)
    .sort((first, second) => {
      if (Boolean(second.imageUrl) !== Boolean(first.imageUrl)) {
        return Number(Boolean(second.imageUrl)) - Number(Boolean(first.imageUrl));
      }

      return first.name.localeCompare(second.name, 'fr');
    })
    .slice(0, 6);
};

const MobileSectionHeader = ({ title, to }) => (
  <div className="mb-3 flex items-center justify-between">
    <h2 className="text-base font-bold text-slate-900">{title}</h2>
    {to ? (
      <Link to={to} className="text-xs font-semibold text-primary-600">
        Voir tout
      </Link>
    ) : null}
  </div>
);

const MobileProductCard = ({ product, formatCurrency }) => {
  const fallbackPresentation = getCategoryPresentation(product.category || product.name);
  const FallbackIcon = fallbackPresentation.icon;

  return (
    <Link
      to={`/catalog?productId=${encodeURIComponent(product.id)}`}
      className="w-[148px] shrink-0 overflow-hidden rounded-[22px] border border-slate-200 bg-white shadow-[0_10px_30px_rgba(15,23,42,0.05)]"
    >
      <div className="relative h-28 overflow-hidden bg-slate-100">
        {product.imageUrl ? (
          <img
            src={product.imageUrl}
            alt={product.name}
            className="h-full w-full object-cover"
            loading="lazy"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-slate-900 via-slate-700 to-slate-500">
            <FallbackIcon className="h-10 w-10 text-white/90" />
          </div>
        )}
      </div>
      <div className="space-y-1.5 p-3">
        <p className="line-clamp-2 text-sm font-semibold leading-5 text-slate-900">{product.name}</p>
        <p className="text-[11px] text-slate-400">A partir de</p>
        <div className="flex items-center justify-between gap-2">
          <p className="text-xs font-semibold text-primary-600">{formatCurrency(product.minPrice).replace('DZD', 'DA')}</p>
          <div className="flex h-6 w-6 items-center justify-center rounded-full bg-slate-100 text-slate-400">
            <ArrowRight className="h-3.5 w-3.5" />
          </div>
        </div>
      </div>
    </Link>
  );
};

const DesktopSectionHeader = ({ title, to }) => (
  <div className="mb-5 flex items-center justify-between">
    <h2 className="text-[28px] font-bold tracking-tight text-slate-900">{title}</h2>
    {to ? (
      <Link to={to} className="inline-flex items-center gap-2 text-sm font-semibold text-primary-600">
        Voir tout
        <ArrowRight className="h-4 w-4" />
      </Link>
    ) : null}
  </div>
);

const DesktopQuickActionCard = ({ action }) => {
  const Icon = action.icon;
  const content = (
    <div className="flex items-center justify-between rounded-[26px] border border-slate-200 bg-white px-6 py-5 shadow-sm transition hover:border-slate-300 hover:shadow-md">
      <div className="flex items-center gap-4">
        <div className={`flex h-14 w-14 items-center justify-center rounded-3xl ${action.iconClass}`}>
          <Icon className="h-6 w-6" />
        </div>
        <div>
          <p className="text-2xl font-bold tracking-tight text-slate-900">{action.title}</p>
          <p className="mt-1 max-w-[180px] text-sm leading-5 text-slate-500">{action.subtitle}</p>
        </div>
      </div>
      <div className={`flex h-11 w-11 items-center justify-center rounded-full border border-slate-200 bg-white ${action.arrowClass}`}>
        <ArrowRight className="h-4 w-4" />
      </div>
    </div>
  );

  if (action.to) {
    return <Link to={action.to}>{content}</Link>;
  }

  return (
    <button type="button" onClick={action.action} className="text-left">
      {content}
    </button>
  );
};

const DesktopProductCard = ({ product, formatCurrency }) => {
  const fallbackPresentation = getCategoryPresentation(product.category || product.name);
  const FallbackIcon = fallbackPresentation.icon;

  return (
    <Link
      to={`/catalog?productId=${encodeURIComponent(product.id)}`}
      className="overflow-hidden rounded-[26px] border border-slate-200 bg-white shadow-sm transition hover:border-slate-300 hover:shadow-md"
    >
      <div className="h-40 overflow-hidden bg-slate-100">
        {product.imageUrl ? (
          <img
            src={product.imageUrl}
            alt={product.name}
            className="h-full w-full object-cover"
            loading="lazy"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-slate-900 via-slate-700 to-slate-500">
            <FallbackIcon className="h-12 w-12 text-white/90" />
          </div>
        )}
      </div>

      <div className="space-y-2 p-5">
        <p className="line-clamp-2 text-lg font-bold leading-6 text-slate-900">{product.name}</p>
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-slate-400">A partir de</p>
            <p className="mt-1 text-base font-semibold text-primary-600">
              {formatCurrency(product.minPrice).replace('DZD', 'DA')}
            </p>
          </div>
          <div className="flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-slate-50 text-slate-400">
            <ArrowRight className="h-4 w-4" />
          </div>
        </div>
      </div>
    </Link>
  );
};

const getDesktopOrderStatus = (operation = {}) => {
  const status = String(operation.status || '').toLowerCase();

  if (status === 'validated' || status === 'completed') {
    return {
      label: 'Livree',
      className: 'bg-emerald-50 text-emerald-600'
    };
  }

  if (status === 'pending' || status === 'processing') {
    return {
      label: 'En cours',
      className: 'bg-amber-50 text-amber-600'
    };
  }

  if (status === 'failed' || status === 'refused' || status === 'rejected') {
    return {
      label: 'Echouee',
      className: 'bg-rose-50 text-rose-600'
    };
  }

  return {
    label: status || 'OK',
    className: 'bg-slate-100 text-slate-600'
  };
};

const DesktopOrderRow = ({ operation, formatCurrency }) => {
  const activityMeta = getActivityMeta(operation);
  const amountValue = operation.amount || operation.totalCost || 0;
  const visualIcon = activityMeta.icon;
  const VisualIcon = visualIcon;
  const status = getDesktopOrderStatus(operation);

  return (
    <div className="grid grid-cols-[minmax(0,1.6fr)_180px_140px_140px_90px] items-center gap-4 border-t border-slate-100 px-4 py-4 first:border-t-0">
      <div className="flex min-w-0 items-center gap-3">
        <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl ${activityMeta.badgeClass}`}>
          <VisualIcon className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-slate-900">{activityMeta.label}</p>
          <p className="truncate text-xs text-slate-400">
            {operation.id ? `Commande #${String(operation.id).slice(0, 6)}` : 'Commande'}
          </p>
        </div>
      </div>

      <p className="text-sm text-slate-500">{formatRelativeMobileDate(operation.createdAt)}</p>

      <div>
        <span className={`inline-flex rounded-full px-3 py-1.5 text-xs font-semibold ${status.className}`}>
          {status.label}
        </span>
      </div>

      <p className={`text-right text-sm font-bold ${activityMeta.amountClass}`}>
        {activityMeta.amountPrefix}{formatCurrency(amountValue).replace('DZD', 'DA')}
      </p>

      <div className="flex justify-end">
        <Link
          to="/operations"
          className="inline-flex min-w-[74px] items-center justify-center rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 transition hover:border-slate-300 hover:bg-slate-50"
        >
          Voir
        </Link>
      </div>
    </div>
  );
};

const ResellerDashboard = () => {
  const { currentUser, userProfile } = useAuth();
  const { showNotification } = useNotification();
  const navigate = useNavigate();

  const [dashboardData, setDashboardData] = useState({
    balance: 0,
    recentActivity: [],
    pendingTransactions24h: 0,
    completedTransactions24h: 0,
    popularCategories: [],
    popularProducts: []
  });
  const [loading, setLoading] = useState(true);
  const [showContactModal, setShowContactModal] = useState(false);
  const [contactMessage, setContactMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [mobileSearch, setMobileSearch] = useState('');

  const formatCurrency = (amount) => new Intl.NumberFormat('fr-DZ', {
    style: 'currency',
    currency: 'DZD',
    minimumFractionDigits: 0
  }).format(amount || 0);

  const currentBalance = Number.isFinite(Number(userProfile?.balance))
    ? Number(userProfile.balance)
    : 0;

  const loadDashboardData = useCallback(async () => {
    try {
      setLoading(true);

      const [allOperations, activeProducts, activeCategories] = await Promise.all([
        TransactionService.getAllUserTransactions(currentUser.uid),
        ProductService.getActiveProducts(),
        CategoryService.getActiveCategories()
      ]);

      const recentActivity = allOperations.slice(0, 5);
      const last24HoursOperations = allOperations.filter((operation) => isWithinLastHours(operation.createdAt, 24));
      const completedTransactions24h = last24HoursOperations.filter((operation) => {
        const status = String(operation.status || '').toLowerCase();
        return status === 'completed' || status === 'validated';
      }).length;
      const pendingTransactions24h = last24HoursOperations.filter((operation) => {
        const status = String(operation.status || '').toLowerCase();
        return status === 'pending' || status === 'processing';
      }).length;

      setDashboardData({
        balance: 0,
        recentActivity,
        pendingTransactions24h,
        completedTransactions24h,
        popularCategories: buildPopularCategories(activeProducts, activeCategories),
        popularProducts: buildPopularProducts(activeProducts, activeCategories)
      });
    } catch (error) {
      console.error('Erreur lors du chargement des donnees:', error);
      showNotification('Erreur lors du chargement des donnees', 'error');
    } finally {
      setLoading(false);
    }
  }, [currentUser?.uid, showNotification]);

  useEffect(() => {
    if (currentUser?.uid && userProfile?.id) {
      loadDashboardData();
    }
  }, [currentUser?.uid, loadDashboardData, userProfile?.id]);

  useEffect(() => {
    if (userProfile?.balance !== undefined) {
      setDashboardData((current) => ({
        ...current,
        balance: userProfile.balance || 0
      }));
    }
  }, [userProfile?.balance]);

  const handleSendContactMessage = async () => {
    if (!contactMessage.trim()) return;

    try {
      setSendingMessage(true);
      await new Promise((resolve) => setTimeout(resolve, 1000));
      showNotification('Message envoye avec succes', 'success');
      setContactMessage('');
      setShowContactModal(false);
    } catch (error) {
      console.error('Erreur lors de l envoi du message:', error);
      showNotification('Erreur lors de l envoi du message', 'error');
    } finally {
      setSendingMessage(false);
    }
  };

  const handleMobileSearchSubmit = (event) => {
    event.preventDefault();
    navigate('/catalog');
  };

  const handleOpenBalanceDetails = () => {
    window.dispatchEvent(new Event('open-balance-details'));
  };

  const quickActions = useMemo(() => ([
    {
      title: 'Gift Cards',
      subtitle: 'Acheter des cartes cadeaux',
      to: '/catalog',
      icon: Gift,
      iconClass: 'bg-violet-50 text-violet-600',
      arrowClass: 'text-violet-500'
    },
    {
      title: 'Topup',
      subtitle: 'Recharger des jeux et services',
      to: '/topup',
      icon: Zap,
      iconClass: 'bg-blue-50 text-blue-600',
      arrowClass: 'text-blue-500'
    },
    {
      title: 'Mes codes',
      subtitle: 'Voir tous vos codes achetes',
      to: '/card-history',
      icon: Package,
      iconClass: 'bg-emerald-50 text-emerald-600',
      arrowClass: 'text-emerald-500'
    },
    {
      title: 'Recharger',
      subtitle: 'Ajouter du solde a votre compte',
      action: () => setShowContactModal(true),
      icon: Wallet,
      iconClass: 'bg-amber-50 text-amber-600',
      arrowClass: 'text-amber-500'
    }
  ]), [setShowContactModal]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <LoadingSpinner size="xl" />
      </div>
    );
  }

  return (
    <div className="space-y-4 overflow-x-hidden sm:space-y-6">
      <div className="lg:hidden">
        <div className="space-y-4">
          <div className="rounded-[28px] border border-slate-200 bg-white p-4 shadow-sm">
            <div className="mb-3">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Dashboard</p>
              <h1 className="mt-1 text-xl font-bold text-slate-900">
                Bonjour, {userProfile?.firstName || 'Revendeur'}
              </h1>
            </div>

            <button
              type="button"
              onClick={handleOpenBalanceDetails}
              className="flex w-full items-center justify-between rounded-2xl border border-emerald-100 bg-emerald-50 px-4 py-4"
            >
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-white text-emerald-600 shadow-sm">
                  <Wallet className="h-4 w-4" />
                </div>
                <div className="text-left">
                  <p className="text-xs font-medium text-slate-500">Solde disponible</p>
                  <p className="mt-1 text-2xl font-bold text-emerald-600">
                    {formatCurrency(currentBalance).replace('DZD', 'DA')}
                  </p>
                </div>
              </div>
              <ArrowRight className="h-4 w-4 text-slate-400" />
            </button>

            <form onSubmit={handleMobileSearchSubmit} className="mt-4">
              <label className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                <Search className="h-4 w-4 text-slate-400" />
                <input
                  value={mobileSearch}
                  onChange={(event) => setMobileSearch(event.target.value)}
                  placeholder="Rechercher un produit, une categorie ou un topup..."
                  className="w-full bg-transparent text-sm text-slate-700 outline-none placeholder:text-slate-400"
                />
              </label>
            </form>
          </div>

          <div className="grid grid-cols-2 gap-3">
            {quickActions.map((action) => {
              const Icon = action.icon;
              const content = (
                <div className="rounded-[22px] border border-slate-200 bg-white p-4 shadow-sm transition hover:border-slate-300">
                  <div className={`flex h-11 w-11 items-center justify-center rounded-2xl ${action.iconClass}`}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <p className="mt-4 text-sm font-semibold text-slate-900">{action.title}</p>
                  <p className="mt-1 text-xs text-slate-400">{action.subtitle}</p>
                  <div className="mt-4 flex justify-end">
                    <div className={`flex h-6 w-6 items-center justify-center rounded-full bg-slate-50 ${action.arrowClass}`}>
                      <ArrowRight className="h-3.5 w-3.5" />
                    </div>
                  </div>
                </div>
              );

              if (action.to) {
                return (
                  <Link key={action.title} to={action.to}>
                    {content}
                  </Link>
                );
              }

              return (
                <button key={action.title} type="button" onClick={action.action} className="text-left">
                  {content}
                </button>
              );
            })}
          </div>

          <div className="rounded-[28px] border border-slate-200 bg-white p-4 shadow-sm">
            <MobileSectionHeader title="Categories populaires" to="/catalog" />
            {dashboardData.popularCategories.length > 0 ? (
              <div className="grid grid-cols-5 gap-2.5">
                {dashboardData.popularCategories.slice(0, 5).map((category) => {
                  const presentation = getCategoryPresentation(category.name);
                  const Icon = presentation.icon;

                  return (
                    <Link
                      key={category.id}
                      to={`/catalog?category=${encodeURIComponent(category.name)}`}
                      className="rounded-[18px] border border-slate-100 bg-slate-50 px-2 py-3 text-center"
                    >
                      <div className={`mx-auto flex h-10 w-10 items-center justify-center rounded-2xl ${presentation.iconClass}`}>
                        <Icon className="h-4 w-4" />
                      </div>
                      <p className="mt-2 line-clamp-2 text-[11px] font-semibold leading-4 text-slate-800">{category.name}</p>
                      <p className="mt-1 text-[10px] text-slate-400">{category.count} produits</p>
                    </Link>
                  );
                })}
              </div>
            ) : (
              <div className="rounded-2xl border border-dashed border-slate-200 px-4 py-8 text-center text-sm text-slate-500">
                Aucune categorie disponible.
              </div>
            )}
          </div>

          <div className="rounded-[28px] border border-slate-200 bg-white p-4 shadow-sm">
            <MobileSectionHeader title="Produits populaires" to="/catalog" />
            {dashboardData.popularProducts.length > 0 ? (
              <div className="scrollbar-none -mx-4 flex gap-3 overflow-x-auto px-4 pb-1">
                {dashboardData.popularProducts.slice(0, 5).map((product) => (
                  <MobileProductCard
                    key={product.id}
                    product={product}
                    formatCurrency={formatCurrency}
                  />
                ))}
              </div>
            ) : (
              <div className="rounded-2xl border border-dashed border-slate-200 px-4 py-8 text-center text-sm text-slate-500">
                Aucun produit disponible.
              </div>
            )}
          </div>

          <div className="rounded-[28px] border border-slate-200 bg-white p-4 shadow-sm">
            <MobileSectionHeader title="Dernieres commandes" to="/operations" />
            <div className="space-y-3">
              {dashboardData.recentActivity.length > 0 ? (
                dashboardData.recentActivity.slice(0, 4).map((operation) => {
                  const activityMeta = getActivityMeta(operation);
                  const amountValue = operation.amount || operation.totalCost || 0;
                  const ActivityIcon = activityMeta.icon;

                  return (
                    <div
                      key={operation.id}
                      className="flex items-center justify-between gap-3 rounded-2xl border border-slate-100 bg-white p-3"
                    >
                      <div className="flex min-w-0 items-center gap-3">
                        <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl ${activityMeta.badgeClass}`}>
                          <ActivityIcon className="h-4 w-4" />
                        </div>
                        <div className="min-w-0">
                          <p className="truncate text-sm font-semibold text-slate-900">{activityMeta.label}</p>
                          <p className="truncate text-[11px] text-slate-400">
                            {operation.id ? `Commande #${String(operation.id).slice(0, 6)}` : 'Commande'}
                          </p>
                          <p className="mt-1 text-[11px] text-slate-400">{formatRelativeMobileDate(operation.createdAt)}</p>
                        </div>
                      </div>

                      <div className="shrink-0 text-right">
                        <p className={`text-sm font-bold ${activityMeta.amountClass}`}>
                          {activityMeta.amountPrefix}{formatCurrency(amountValue).replace('DZD', 'DA')}
                        </p>
                        <p className={`mt-1 inline-flex rounded-full px-2.5 py-1 text-[10px] font-semibold ${activityMeta.badgeClass}`}>
                          {operation.status === 'validated'
                            ? 'Livree'
                            : operation.status === 'completed'
                              ? 'Livree'
                              : operation.status === 'processing'
                                ? 'En cours'
                                : operation.status === 'pending'
                                  ? 'En attente'
                                  : operation.status || 'OK'}
                        </p>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="rounded-2xl border border-dashed border-slate-200 px-4 py-8 text-center text-sm text-slate-500">
                  Aucune commande recente.
                </div>
              )}
            </div>

            <Link
              to="/operations"
              className="mt-4 flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-semibold text-slate-700"
            >
              Voir toutes mes commandes
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </div>

      <div className="hidden lg:block">
        <div className="space-y-6 rounded-[30px] border border-slate-200 bg-white p-6 shadow-sm xl:p-8">
          <form onSubmit={handleMobileSearchSubmit}>
            <label className="flex items-center gap-4 rounded-[22px] border border-slate-200 bg-slate-50 px-5 py-4">
              <Search className="h-5 w-5 text-slate-400" />
              <input
                value={mobileSearch}
                onChange={(event) => setMobileSearch(event.target.value)}
                placeholder="Rechercher un produit, une categorie ou un topup..."
                className="w-full bg-transparent text-base text-slate-700 outline-none placeholder:text-slate-400"
              />
            </label>
          </form>

          <div className="grid gap-4 xl:grid-cols-4">
            {quickActions.map((action) => (
              <DesktopQuickActionCard key={action.title} action={action} />
            ))}
          </div>

          <div className="rounded-[28px] border border-slate-200 bg-white p-6">
            <DesktopSectionHeader title="Categories populaires" to="/catalog" />
            {dashboardData.popularCategories.length > 0 ? (
              <div className="grid gap-4 xl:grid-cols-5">
                {dashboardData.popularCategories.slice(0, 5).map((category) => {
                  const presentation = getCategoryPresentation(category.name);
                  const Icon = presentation.icon;

                  return (
                    <Link
                      key={category.id}
                      to={`/catalog?category=${encodeURIComponent(category.name)}`}
                      className="flex items-center justify-between rounded-[24px] border border-slate-200 bg-slate-50/70 px-5 py-5 transition hover:border-slate-300 hover:bg-white"
                    >
                      <div className="flex items-center gap-4">
                        <div className={`flex h-12 w-12 items-center justify-center rounded-3xl ${presentation.iconClass}`}>
                          <Icon className="h-5 w-5" />
                        </div>
                        <div>
                          <p className="text-xl font-bold tracking-tight text-slate-900">{category.name}</p>
                          <p className="mt-1 text-sm text-slate-500">{category.count} produits</p>
                        </div>
                      </div>
                      <div className={`flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white ${presentation.actionClass}`}>
                        <ArrowRight className="h-4 w-4" />
                      </div>
                    </Link>
                  );
                })}
              </div>
            ) : (
              <div className="rounded-2xl border border-dashed border-slate-200 px-6 py-10 text-center">
                <Package className="mx-auto h-10 w-10 text-slate-300" />
                <p className="mt-3 text-sm text-slate-500">Aucune categorie disponible pour le moment.</p>
              </div>
            )}
          </div>

          <div className="rounded-[28px] border border-slate-200 bg-white p-6">
            <DesktopSectionHeader title="Produits populaires" to="/catalog" />
            {dashboardData.popularProducts.length > 0 ? (
              <div className="grid gap-4 xl:grid-cols-4">
                {dashboardData.popularProducts.slice(0, 4).map((product) => (
                  <DesktopProductCard
                    key={product.id}
                    product={product}
                    formatCurrency={formatCurrency}
                  />
                ))}
              </div>
            ) : (
              <div className="rounded-2xl border border-dashed border-slate-200 px-6 py-10 text-center">
                <Package className="mx-auto h-10 w-10 text-slate-300" />
                <p className="mt-3 text-sm text-slate-500">Aucun produit disponible pour le moment.</p>
              </div>
            )}
          </div>

          <div className="rounded-[28px] border border-slate-200 bg-white p-3">
            <div className="px-3 pt-3">
              <DesktopSectionHeader title="Dernieres commandes" to="/operations" />
            </div>

            {dashboardData.recentActivity.length > 0 ? (
              <div className="rounded-[22px] border border-slate-100 bg-white">
                {dashboardData.recentActivity.slice(0, 3).map((operation) => (
                  <DesktopOrderRow
                    key={operation.id}
                    operation={operation}
                    formatCurrency={formatCurrency}
                  />
                ))}
              </div>
            ) : (
              <div className="mx-3 rounded-2xl border border-dashed border-slate-200 px-6 py-10 text-center">
                <CheckCircle className="mx-auto h-10 w-10 text-slate-300" />
                <p className="mt-3 text-sm text-slate-500">Aucune activite recente pour le moment.</p>
              </div>
            )}

            <div className="px-3 pb-3 pt-4">
              <Link
                to="/operations"
                className="flex items-center justify-center gap-2 rounded-[18px] border border-slate-200 bg-slate-50 px-4 py-4 text-sm font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-100"
              >
                Voir toutes mes commandes
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </div>

      {showContactModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
              <h3 className="text-lg font-semibold text-gray-900">Contacter l administrateur</h3>
              <button
                onClick={() => setShowContactModal(false)}
                className="text-gray-400 transition-colors hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4 px-5 py-5">
              <div>
                <p className="text-sm font-medium text-gray-700">
                  De: {userProfile?.firstName} {userProfile?.lastName}
                </p>
                <p className="mt-1 text-sm text-gray-500">Sujet: Demande de recharge de solde</p>
              </div>

              <div>
                <label htmlFor="message" className="mb-2 block text-sm font-medium text-gray-700">
                  Message
                </label>
                <textarea
                  id="message"
                  rows={4}
                  value={contactMessage}
                  onChange={(event) => setContactMessage(event.target.value)}
                  className="w-full rounded-xl border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="Decrivez votre demande de recharge..."
                />
              </div>

              <div className="flex flex-col gap-3 sm:flex-row">
                <button
                  onClick={() => setShowContactModal(false)}
                  className="flex-1 rounded-xl border border-gray-300 px-4 py-2.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
                >
                  Annuler
                </button>
                <button
                  onClick={handleSendContactMessage}
                  disabled={sendingMessage || !contactMessage.trim()}
                  className="flex flex-1 items-center justify-center rounded-xl bg-primary-600 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {sendingMessage ? (
                    <>
                      <div className="mr-2 h-4 w-4 animate-spin rounded-full border-b-2 border-white" />
                      Envoi...
                    </>
                  ) : (
                    <>
                      <Send className="mr-2 h-4 w-4" />
                      Envoyer
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ResellerDashboard;
